import React from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import styles from './TunnelStonePickerModal.module.css';
import { useTranslation } from '@/hooks/useTranslation';
import { CommentStoneOption } from '@/utils/commentStones';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import A_CircleButton from '../A_Atoms/A_CircleButton/A_CircleButton';

export interface TunnelStonePickerModalProps {
  isOpen: boolean;
  stoneOptions: CommentStoneOption[];
  selectedStoneId: string;
  onSelect: (stone: CommentStoneOption) => void;
  onClose: () => void;
  disabled?: boolean;
  balance?: number | null;
}

const formatPriceRu = (price: number) => `${price.toLocaleString('ru-RU')} $`;

export default function TunnelStonePickerModal({
  isOpen,
  stoneOptions,
  selectedStoneId,
  onSelect,
  onClose,
  disabled = false,
  balance,
}: TunnelStonePickerModalProps) {
  const t = useTranslation();

  const formatPrice = (price: number) =>
    price > 0 ? formatPriceRu(price) : t('capsuleReactions.freeStone');

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.sheet} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.header_left}>
            <span className={cn(styles.title, 'text-p-6')}>
              {t('capsuleReactions.selectStoneLabel')}
            </span>
            {balance != null && (
              <span className={cn(styles.balance, 'text-p-7')}>
                {t('capsuleReactions.balanceLabel')}: {formatPriceRu(balance)}
              </span>
            )}
          </div>
          <A_CircleButton
            icon={<Q_Icon.CrossIcon width={12} height={12} fill='none' />}
            onClick={onClose}
            variant='secondary'
            size='small'
          />
        </div>

        <div className={styles.list}>
          {stoneOptions.map(stone => (
            <button
              key={stone.id}
              type='button'
              className={cn(styles.item, {
                [styles.item_selected]: stone.id === selectedStoneId,
              })}
              onClick={() => {
                onSelect(stone);
                onClose();
              }}
              disabled={disabled}
            >
              <img
                src={stone.imageUrl}
                alt={stone.name}
                className={styles.item_img}
                draggable={false}
              />
              <div className={styles.item_meta}>
                <span className={cn(styles.item_name, 'text-p-7')}>
                  {stone.name}
                </span>
                <span className={cn(styles.item_price, 'text-caption')}>
                  {formatPrice(stone.price)}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}
